import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Mail, Clock } from "lucide-react";
import { Logo } from "./Logo";
import { Placeholder } from "./Placeholder";
import { navLinks, site } from "../config/siteConfig";

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-dark text-slate-200">
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-4">
        <Logo light />
        <p className="text-sm leading-relaxed text-slate-300">
          A local educational institution serving families in Mundeke Goraya and
          the surrounding Daska and Sialkot communities.
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={site.phoneHref}
            data-testid="footer-call-button"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <Phone className="h-4 w-4" aria-hidden="true" /> Call
          </a>
          <a
            href={site.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp-button"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp
          </a>
        </div>
      </div>

      <nav aria-label="Footer" className="space-y-3">
        <h2 className="font-display text-base font-bold text-white">Explore</h2>
        <ul className="space-y-2 text-sm">
          {navLinks.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                data-testid={`footer-nav-${l.label.toLowerCase()}`}
                className="text-slate-300 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="space-y-3">
        <h2 className="font-display text-base font-bold text-white">Admissions</h2>
        <ul className="space-y-2 text-sm">
          <li>
            <Link to="/admissions" data-testid="footer-admission-enquiry-link" className="text-slate-300 transition-colors hover:text-white">
              Start an Admission Enquiry
            </Link>
          </li>
          <li>
            <Link to="/academics" data-testid="footer-academics-link" className="text-slate-300 transition-colors hover:text-white">
              Academic Programs
            </Link>
          </li>
          <li>
            <Link to="/faqs" data-testid="footer-faqs-link" className="text-slate-300 transition-colors hover:text-white">
              Admission FAQs
            </Link>
          </li>
        </ul>
        <p className="flex items-start gap-2 pt-2 text-sm text-slate-300">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-amber" aria-hidden="true" />
          <Placeholder>{site.openingHours}</Placeholder>
        </p>
      </div>

      <div className="space-y-3 text-sm">
        <h2 className="font-display text-base font-bold text-white">Contact</h2>
        <p className="flex items-start gap-2 text-slate-300">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-amber" aria-hidden="true" />
          <span>
            {site.addressLine1}, {site.addressLine2}, {site.addressLine3}
          </span>
        </p>
        <p className="flex items-center gap-2">
          <Phone className="h-4 w-4 shrink-0 text-brand-amber" aria-hidden="true" />
          <a href={site.phoneHref} data-testid="footer-phone-link" className="text-slate-200 transition-colors hover:text-white">
            {site.phoneDisplay}
          </a>
        </p>
        <p className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 shrink-0 text-brand-amber" aria-hidden="true" />
          <a href={site.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="text-slate-200 transition-colors hover:text-white">
            {site.whatsappDisplay}
          </a>
        </p>
        <p className="flex items-center gap-2">
          <Mail className="h-4 w-4 shrink-0 text-brand-amber" aria-hidden="true" />
          <Placeholder>{site.email}</Placeholder>
        </p>
        <a
          href={site.directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-directions-link"
          className="inline-flex items-center gap-1 font-semibold text-brand-amber transition-colors hover:text-amber-300"
        >
          Open in Google Maps
        </a>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-center text-xs text-slate-400 sm:flex-row sm:px-6 sm:text-left">
        <p>© {new Date().getFullYear()} Ansar Public School Mundeke. All rights reserved.</p>
        <p>Details marked with dashed amber labels are editable placeholders pending school verification.</p>
      </div>
    </div>
  </footer>
);
